import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, ExternalLink, Loader2 } from 'lucide-react';
import { notificationsApi, type NotificationDto } from '@/api/notifications.api';
import { AppRoutes } from '@/constants/routes';

const POLL_INTERVAL_MS = 30_000;

// ── helpers ───────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs  < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Notification item ────────────────────────────────────────────────────────

const NotificationItem = ({
  n,
  onRead,
}: {
  n: NotificationDto;
  onRead: (id: string) => void;
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!n.isRead) onRead(n.id);
    if (n.actionUrl) navigate(n.actionUrl);
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
        !n.isRead ? 'bg-blue-50/60 dark:bg-blue-900/10' : ''
      }`}
    >
      <div className="flex items-start gap-2">
        {!n.isRead && (
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
        )}
        <div className={`flex-1 min-w-0 ${n.isRead ? 'pl-3.5' : ''}`}>
          <p className={`text-sm font-medium truncate ${n.isRead ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100'}`}>
            {n.title}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
            {n.message}
          </p>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
            {timeAgo(n.createdAt)}
            {n.module && <span className="ml-1.5 px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[9px] uppercase tracking-wide">{n.module}</span>}
          </p>
        </div>
        {n.actionUrl && <ExternalLink size={12} className="flex-shrink-0 mt-1 text-gray-300 dark:text-gray-600" />}
      </div>
    </button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export const NotificationBell = () => {
  const navigate  = useNavigate();
  const panelRef  = useRef<HTMLDivElement>(null);

  const [open,         setOpen]         = useState(false);
  const [unreadCount,  setUnreadCount]  = useState(0);
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [loading,      setLoading]      = useState(false);

  // ── poll unread count ─────────────────────────────────────────────────────
  const fetchCount = useCallback(async () => {
    try {
      const count = await notificationsApi.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // silent — badge just won't update
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const timer = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchCount]);

  // ── open panel: load notifications ───────────────────────────────────────
  const openPanel = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    try {
      const items = await notificationsApi.getMyNotifications(false, 20);
      setNotifications(items);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── click outside to close ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node))
        setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── mark single as read ───────────────────────────────────────────────────
  const handleRead = useCallback(async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* silent */ }
  }, []);

  // ── mark all as read ──────────────────────────────────────────────────────
  const handleMarkAllRead = useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  }, []);

  return (
    <div ref={panelRef} className="relative">
      {/* Bell button */}
      <button
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        onClick={() => (open ? setOpen(false) : openPanel())}
        className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-0.5 leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl z-50 animate-fade-in overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-xs font-medium text-blue-600 dark:text-blue-400">
                  {unreadCount} unread
                </span>
              )}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                title="Mark all as read"
                className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <CheckCheck size={13} />
                Mark all read
              </button>
            )}
          </div>

          {/* Body */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-gray-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell size={28} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">No notifications</p>
              </div>
            ) : (
              notifications.map((n) => (
                <NotificationItem key={n.id} n={n} onRead={handleRead} />
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2">
            <button
              onClick={() => { setOpen(false); navigate(AppRoutes.NOTIFICATIONS); }}
              className="w-full text-center text-xs text-blue-600 dark:text-blue-400 hover:underline py-1"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
